/** @jsxImportSource @emotion/react */
import React, { useState, useRef, useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  useSetTitleOnMount,
  useSetDescriptionOnMount,
} from "components/hooks/useSetTitle";
import { useTrackWithMixpanelOnce } from "trackers/mixpanel";
import { useReportsData } from "pages/reports/bi-dashboard-next/hooks/useReportsData";
import {
  ReportCriteriaDTO,
  defaultReportCriteria,
} from "pages/reports/bi-dashboard-next/models/ReportCriteriaDTO";
import { DistinctTagDTO } from "pages/reports/bi-dashboard-next/models/DistinctTagDTO";
import { ReportTypeaheadDTO } from "pages/reports/bi-dashboard-next/models/SearchTypeaheadDTO";
import { ReportTabType } from "pages/reports/bi-dashboard-next/models/ReportTabType";
import { Alert, AlertVariant } from "components/atoms/Alert.atom";
import { ReportsContent } from "pages/reports/bi-dashboard-next/components/organisms/Reports.Content.organism";
import { ReportsLeftNavbar } from "pages/reports/bi-dashboard-next/components/organisms/Reports.LeftNavbar.organism";
import { ReportsTopBar } from "pages/reports/bi-dashboard-next/components/organisms/Reports.TopBar.organism";
import { BrowserStorage } from "utils/browser-storage.utils";

interface ReportsProps {
  currentUser: any;
}

const Reports: React.FC<ReportsProps> = ({ currentUser }) => {
  const { t } = useTranslation("reports");
  const dispatch = useDispatch();

  useSetTitleOnMount(t("reports:Analytics Hub"));
  useSetDescriptionOnMount(
    t(
      "reports:Business intelligence reporting platform for organizational analytics",
    ),
  );
  useTrackWithMixpanelOnce("Viewed Page: BI / Reports / Dashboard");

  const [activeTab, setActiveTab] = useState<ReportTabType>(
    ReportTabType.PUBLIC,
  );

  const [viewMode, setViewMode] = useState(() => {
    return BrowserStorage.reportsViewMode || "row";
  });

  const [currentSection, setCurrentSection] = useState<
    "favorites" | "recentlyOpened" | "scheduled" | "openedThisWeek" | "all"
  >("all");
  const [fade, setFade] = useState(false);
  const prevViewMode = useRef(viewMode);

  const [searchValue, setSearchValue] = useState<string>("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const [criteria, setCriteria] = useState<ReportCriteriaDTO>(
    defaultReportCriteria,
  );
  const [debouncedCriteria, setDebouncedCriteria] = useState<ReportCriteriaDTO>(
    defaultReportCriteria,
  );
  const [sortBy, setSortBy] = useState<string>("name");
  const [sortDirection, setSortDirection] = useState<string>("asc");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedCriteria(criteria);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [criteria]);

  useEffect(() => {
    if (activeTab === ReportTabType.MY && currentUser) {
      setCriteria({
        isPrivate: true,
      });
      setSelectedTags([]);
    } else {
      setCriteria({
        isPrivate: false,
      });
      setSelectedTags([]);
    }
  }, [activeTab, currentUser]);

  useEffect(() => {
    BrowserStorage.reportsViewMode = viewMode;
  }, [viewMode]);

  // Prune old reports when the page loads
  useEffect(() => {
    BrowserStorage.pruneOldOpenedReports();
  }, []);

  const {
    isLoading,
    reports,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    error,
    isError,
    toggleFavorite,
    isTogglingFavorite,
  } = useReportsData(debouncedCriteria, sortBy, sortDirection);

  const handleViewModeChange = (value: string) => {
    if (value !== viewMode) {
      setFade(true);
      setTimeout(() => {
        setViewMode(value);
        setFade(false);
        prevViewMode.current = value;
      }, 200);
    }
  };

  const handleTagClick = (tag: DistinctTagDTO) => {
    const tagIndex = selectedTags.indexOf(tag.tag);
    let newSelectedTags: string[];

    if (tagIndex >= 0) {
      newSelectedTags = selectedTags.filter((t) => t !== tag.tag);
    } else {
      newSelectedTags = [...selectedTags, tag.tag];
    }

    setSelectedTags(newSelectedTags);

    setCriteria({
      ...criteria,
      tagNamesIn: newSelectedTags.length > 0 ? newSelectedTags : undefined,
    });
  };

  const handleAllClick = () => {
    setSelectedTags([]);
    setCriteria({
      ...defaultReportCriteria,
    });
    setSearchValue("");
    setCurrentSection("all");
  };

  const handleArchiveClick = () => {
    setSelectedTags([]);
    setCriteria({
      enabled: false,
    });
    setSearchValue("");
  };

  const handleRecentlyAddedClick = () => {
    setSelectedTags([]);
    const twoWeeksAgo = new Date();
    twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
    const twoWeeksAgoStr = twoWeeksAgo.toISOString();

    setCriteria({
      enabled: true,
      createdAtFrom: twoWeeksAgoStr,
    });
    setSearchValue("");
    setCurrentSection("recentlyOpened");
  };

  const handleFavoritesClick = () => {
    setSelectedTags([]);
    setCriteria({
      ...defaultReportCriteria,
      favorited: true,
    });
    setSearchValue("");
    setCurrentSection("favorites");
  };

  const handleScheduledEmailClick = () => {
    setSelectedTags([]);
    setCriteria({
      ...defaultReportCriteria,
      hasEmailSchedules: true,
    });
    setSearchValue("");
    setCurrentSection("scheduled");
  };

  const handleOpenedThisWeekClick = () => {
    setSelectedTags([]);
    const reportNumbers = BrowserStorage.getReportNumbersOpenedThisWeek();

    setCriteria({
      ...defaultReportCriteria,
      reportNumbersIn: reportNumbers.length > 0 ? reportNumbers : ["0"],
    });
    setSearchValue("");
    setCurrentSection("openedThisWeek");
  };

  const handleTagFilter = (tagName: string) => {
    const newTagNames = [tagName];
    setSelectedTags(newTagNames);
    setCriteria({
      ...criteria,
      tagNamesIn: newTagNames,
    });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    if (e.target.value === "") {
      setCriteria({
        ...defaultReportCriteria,
      });
      setSelectedTags([]);
    }
  };

  const handleSelectTag = (tag: DistinctTagDTO) => {
    const newTagNames = [tag.tag];
    setCriteria({
      tagNamesIn: newTagNames,
    });
    setSelectedTags([tag.tag]);
    setSearchValue(tag.tag);
  };

  const handleSelectReport = (report: ReportTypeaheadDTO) => {
    setSearchValue(report.name);
    BrowserStorage.addOpenedReport(report.reportNumber);
    dispatch({
      type: "REPORT_DETAIL",
      payload: { reportId: report.reportNumber },
    });
  };

  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  return (
    <div
      css={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100%",
      }}
    >
      <ReportsTopBar
        activeTab={activeTab}
        onTabChange={(tab) => {
          setActiveTab(tab);
        }}
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        onSelectTag={handleSelectTag}
        onSelectReport={handleSelectReport}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
      />

      <div
        css={{
          display: "grid",
          padding: 10,
          gridTemplateColumns:
            activeTab === ReportTabType.MY ? "0fr 1fr" : "340px 1fr",
          transition: "all .5s ease-in-out",
          gap: 10,
          width: "100%",
          flex: 1,
          minHeight: 0,
          overflow: "auto",
        }}
      >
        <div
          css={{
            overflow: "auto",
            transition: "all .5s ease-in-out",
            maxWidth: activeTab === ReportTabType.MY ? "0" : "340px",
            opacity: activeTab === ReportTabType.MY ? 0 : 1,
            transform:
              activeTab === ReportTabType.MY
                ? "translateX(-100%)"
                : "translateX(0)",
            visibility: activeTab === ReportTabType.MY ? "hidden" : "visible",
            transitionProperty: "transform, opacity, max-width, visibility",
            transitionDelay:
              activeTab === ReportTabType.MY ? "0s, 0s, 0s, .5s" : "0s",
          }}
        >
          <ReportsLeftNavbar
            selectedTags={selectedTags}
            criteria={criteria}
            onAllClick={handleAllClick}
            onTagClick={handleTagClick}
            onArchiveClick={handleArchiveClick}
            onRecentlyAddedClick={handleRecentlyAddedClick}
            onFavoritesClick={handleFavoritesClick}
            onScheduledEmailClick={handleScheduledEmailClick}
            onOpenedThisWeekClick={handleOpenedThisWeekClick}
          />
        </div>
        {isError ? (
          <Alert
            show={isError}
            variant={AlertVariant.Danger}
            css={{
              maxHeight: "50px",
            }}
          >
            {t("Error fetching reports. Please try again later.")}
          </Alert>
        ) : (
          <ReportsContent
            viewMode={viewMode}
            fade={fade}
            isLoading={isLoading}
            reports={reports}
            loadMore={loadMore}
            isFetchingNextPage={isFetchingNextPage}
            onTagClick={handleTagFilter}
            refetch={refetch}
            toggleFavorite={toggleFavorite}
            isTogglingFavorite={isTogglingFavorite}
            currentSection={currentSection}
            activeTab={activeTab}
          />
        )}
      </div>
    </div>
  );
};

export default Reports;
